// Fase 1A — Servicio operativo de SOLO LECTURA (thin wrappers de axios).
// Encapsula lecturas/listados/búsquedas de: aforo, vehículos activos/patio, tickets cobrados,
// búsqueda de ticket, listado de Valet y catálogo de productos/servicios + tarifas.
// NO incluye escrituras (ingreso, checkout, cobros, caja, estados de valet, ventas, pensión).
import api from "@/lib/api";

// POST idempotente (Ticket/Folio 1:1 ante reintentos por mala señal): genera una Idempotency-Key
// por operación y la REUTILIZA en reintentos. Ante error de RED (sin `response`: timeout) reintenta
// con la MISMA clave hasta 3 veces con backoff; el backend no duplica ni re-descuenta folios.
// Errores con respuesta HTTP (4xx/5xx) NO se reintentan.
export const nuevaIdemKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

// UNA sola Idempotency-Key por operación. Si el caller provee `opts.idemKey` (misma recepción), se
// REUTILIZA en TODOS los reintentos (timeout/mala señal/retry manual) para que el backend nunca cree
// un 2.º ticket; si no se provee, se genera una clave nueva. La clave NO cambia entre reintentos.
const postIdempotente = async (url, body, opts = {}, intentos = 3) => {
  const key = opts.idemKey || nuevaIdemKey();
  const config = opts.config;
  let ultimoError;
  for (let i = 0; i < intentos; i++) {
    try {
      return await api.post(url, body, {
        ...(config || {}),
        headers: { ...(config?.headers || {}), "Idempotency-Key": key },
      });
    } catch (e) {
      ultimoError = e;
      if (e?.response) throw e; // respuesta del servidor => no reintentar
      await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  throw ultimoError;
};

export const getAforo = (idSucursal) =>
  api.get(`/operativa/aforo?id_sucursal=${idSucursal}`);

export const getAutosEnPatio = (idSucursal) =>
  api.get(`/operativa/autos-en-patio?id_sucursal=${idSucursal}`);

export const getInventarioPatio = (idSucursal) =>
  api.get(`/operativa/inventario-patio?id_sucursal=${idSucursal}`);

// Tickets cobrados recientes. `id_caja` y `limit` opcionales (respetan el orden de query original).
export const getTicketsCobrados = (idSucursal, { idCaja, limit } = {}) => {
  let url = `/operativa/tickets-cobrados?id_sucursal=${idSucursal}`;
  if (idCaja) url += `&id_caja=${idCaja}`;
  if (limit != null) url += `&limit=${limit}`;
  return api.get(url);
};

export const buscarTicket = (idSucursal, q) =>
  api.get(`/operativa/ticket-buscar?id_sucursal=${idSucursal}&q=${encodeURIComponent(q)}`);

export const listValet = (idSucursal) =>
  api.get(`/operativa/valet?id_sucursal=${idSucursal}`);

export const getCatalogoLocal = (idSucursal) =>
  api.get(`/operativa/catalogo-local?id_sucursal=${idSucursal}`);

export const buscarProductos = (idSucursal, q) =>
  api.get(`/operativa/productos?id_sucursal=${idSucursal}&q=${encodeURIComponent(q)}`);

export const getProductoPorCodigo = (idSucursal, codigo) =>
  api.get(`/operativa/producto-codigo?id_sucursal=${idSucursal}&codigo=${encodeURIComponent(codigo)}`);

export const getTarifas = (idSucursal) =>
  api.get(`/operativa/tarifas?id_sucursal=${idSucursal}`);

export const getTarifasVehiculos = (idSucursal) =>
  api.get(`/operativa/tarifas-vehiculos?id_sucursal=${idSucursal}`);

// --- Fase 1B — Escrituras operativas de bajo riesgo (thin wrappers de axios). ---
// SOLO encapsulan la llamada HTTP. El procesamiento (apertura de cajón, outbox/offline,
// recibos, MP Point) permanece en el contenedor. Se conserva el contrato y la respuesta axios.
// NO incluye: checkout, caja (abrir/cerrar), gastos, pensión, Mercado Pago Point ni offline.

// Ingreso/alta de vehículo de estacionamiento (genera ticket + folio).
export const crearTicketIngreso = (body) => postIdempotente("/operativa/tickets", body);

// Venta de carrito de productos (Retail legacy). `config` opcional passthrough a axios.
export const crearVenta = (idSucursal, body) =>
  api.post(`/operativa/ventas?id_sucursal=${idSucursal}`, body);

// Cobro directo de un producto suelto.
export const venderProducto = (payload) => api.post("/operativa/venta-producto", payload);

// Cobro directo de un servicio suelto (Baños/Lavado, etc.).
export const venderServicio = (payload) => api.post("/operativa/venta-servicio", payload);

// Venta rápida (Retail): carrito + método de pago en una transacción.
export const ventaRapida = (body) => api.post("/operativa/venta-rapida", body);

// Evidencia de acreditación (imagen) asociada a un ticket. Compartida por Terminal y Valet.
export const crearEvidencia = (body) => api.post("/operativa/evidencias", body);

// Recepción/alta de vehículo Valet.
export const recibirValet = (body, opts) => postIdempotente("/operativa/valet", body, opts);

// Cambio de estado del vehículo Valet (Recibido → En resguardo → Solicitado → …).
export const cambiarEstadoValet = (idDetalle, body) =>
  api.put(`/operativa/valet/${idDetalle}/estado`, body);

// Nota de incidencia asociada al ticket del Valet.
export const guardarNotaValet = (idDetalle, body) =>
  api.put(`/operativa/valet/${idDetalle}/nota`, body);

// --- Fase 1C-A — Checkout / cobro central ---
// Cobro central del ticket (estacionamiento y Valet): efectivo/tarjeta, cortesía y boleto perdido.
// Thin wrapper: NO altera cálculos, métodos de pago, folios, recibos ni contratos. Devuelve la
// respuesta axios tal cual. La lógica offline (enqueue), apertura de cajón y recibos siguen en el contenedor.
export const checkoutTicket = (body) => api.post("/operativa/checkout", body);

// --- Fase 1C-B1 — Caja: apertura, cierre y detección de caja activa ---
// Thin wrappers: NO alteran cálculos/reglas de caja, fondo inicial, efectivo esperado/contado,
// diferencias, impresión del corte, apertura de cajón, permisos ni estados. Devuelven la respuesta axios tal cual.
export const getCajaActiva = (idSucursal) =>
  api.get(`/operativa/caja-activa?id_sucursal=${idSucursal}`);
export const abrirCaja = (body) => api.post("/operativa/caja/abrir", body);
export const cerrarCaja = (body) => api.post("/operativa/caja/cerrar", body);

// --- Fase 1C-B2 — Gastos operativos de caja ---
// Thin wrapper: registra un gasto contra la caja abierta. NO altera reglas de caja, permisos,
// validaciones, cálculos ni el resumen/cierre. Devuelve la respuesta axios tal cual.
export const crearGasto = (body) => api.post("/operativa/gastos", body);
